// src/utils/escalation.ts
//
// Pure, unit-testable helpers for the Story 4.4 escalate / reassign surfaces.
// Kept free of React/Supabase so the escalation-preview logic is testable in
// isolation and stays byte-consistent with the server (migration 035).
//
// The client NEVER predicts the resolved individual (AD-2: yak_resolve_assignment
// is EXECUTE-revoked from every client role, and round-robin means the pick isn't
// determined until commit) — it only reads the NEXT layer's position + matchers
// from the rules row, and mirrors the server's terminal rule exactly.

import type { YakRuleLayer } from '../types/yak'

// The next escalation layer for a ticket currently at `currentLayerId`, or null
// when there is no next layer (terminal). Byte-consistent with tx_escalate's
// server rule:
//   * empty layers          → null (terminal)
//   * current = last element → null (terminal)
//   * current NULL or stale  → layers[0] (a new assignment resolves from layer 1)
//   * otherwise              → the element after the current one
export function nextLayerFor(
  layers: YakRuleLayer[] | null | undefined,
  currentLayerId: string | null | undefined
): YakRuleLayer | null {
  const arr = layers ?? []
  if (arr.length === 0) return null

  const lastId = arr[arr.length - 1].layer_id
  // A NULL / stale current layer restarts from layer 1 (never terminal).
  if (!currentLayerId) return arr[0]
  // At the last configured layer ⇒ terminal.
  if (currentLayerId === lastId) return null

  const idx = arr.findIndex(l => l.layer_id === currentLayerId)
  // Stale id (edited out of the array) ⇒ resolve from layer 1.
  if (idx === -1) return arr[0]
  return arr[idx + 1] ?? null
}

// The matcher labels used in the escalate dialog's next-layer preview. Lowercase
// to match the EXPERIENCE Flow 2 copy ("matched by channel, payer"). Same
// vocabulary as yak_user_attributes.attribute_type.
const MATCHER_LABELS: Record<string, string> = {
  brand: 'brand',
  region: 'region',
  channel: 'channel',
  payer: 'payer',
}

// The matcher-summary clause for a layer's match_attributes: "matched by channel,
// payer" for a non-empty set, or "any team member" when the layer matches on no
// attribute (empty array ⇒ everyone at that position is a candidate). NEVER names
// a person — predicting the pick is AD-2's forbidden second matcher.
export function matcherSummary(
  matchAttributes: string[] | null | undefined
): string {
  const attrs = matchAttributes ?? []
  if (attrs.length === 0) return 'any team member'
  const labels = attrs.map(a => MATCHER_LABELS[a] ?? a)
  return `matched by ${labels.join(', ')}`
}

// Minimal shapes matchEscalationNotes needs — an assignment row carries a reason
// + created_at; a message carries a kind + created_at + body. Both come straight
// off the detail bundle (YakAssignmentWithUser / YakMessageWithAuthor).
interface AssignmentLike {
  id: string
  reason: string
  created_at: string | null
}
interface MessageLike {
  kind: string
  created_at: string | null
  body: string
}

// Map each 'escalated' assignment row to its escalation note body (the internal
// note tx_escalate inserted in the SAME transaction, so their created_at strings
// are identical — the display join AC 8 consumes). Keyed by assignment id.
// Tolerant of no match: an escalation without a note simply has no entry, so the
// timeline renders it plain. ISO timestamps from PostgREST are stable for equal
// timestamptz values, so string equality is the whole mechanism (no FK exists).
export function matchEscalationNotes(
  assignments: AssignmentLike[],
  messages: MessageLike[]
): Record<string, string> {
  const notes = messages.filter(
    m => m.kind === 'internal_note' && m.created_at !== null
  )
  const out: Record<string, string> = {}
  for (const a of assignments) {
    if (a.reason !== 'escalated' || a.created_at === null) continue
    const match = notes.find(m => m.created_at === a.created_at)
    if (match) out[a.id] = match.body
  }
  return out
}
